import { useAuth } from '@/hooks/use-auth';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getSystemConfig, getModuleIcon, SYSTEM_CONFIGS } from '@/lib/system-icons';
import { useActiveSystems } from '@/hooks/use-active-systems';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import portalOneLogo from "@/assets/portalone-suite-logo.png";
import {
  Building2,
  Users,
  Layers,
  Plug,
  Settings,
  LogOut,
  LayoutDashboard,
  BarChart3,
  HelpCircle,
  Bell,
  ChevronDown,
  Mail,
  Globe,
  Tags,
  Eye,
  Radio,
  Sun,
  Moon,
  Monitor,
  Receipt,
  Truck,
  Factory,
} from 'lucide-react';
import { useTheme } from '@/contexts/theme-provider';
import { Separator } from '@/components/ui/separator';

const adminNavItems = [
  {
    title: 'Dashboard',
    url: '/admin',
    icon: LayoutDashboard,
    requiredRoles: ['portal_admin', 'license_manager', 'auditor']
  },
  // Leads & CRM removed - system discontinued
  {
    title: 'Usuários Globais',
    url: '/admin/users',
    icon: Users,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'Usuários Demo',
    url: '/admin/demo-users',
    icon: Eye,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'Faturamento',
    url: '/admin/billing',
    icon: Receipt,
    requiredRoles: ['portal_admin', 'license_manager']
  },
  {
    title: 'Relatórios',
    url: '/admin/reports',
    icon: BarChart3,
    requiredRoles: ['portal_admin', 'license_manager', 'auditor']
  },
  {
    title: 'Empresas',
    url: '/admin/companies',
    icon: Building2,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'Notificações',
    url: '/admin/notifications',
    icon: Bell,
    requiredRoles: ['portal_admin']
  },
  // DevOpsONE removed - system discontinued
  // SystemSettingsPage removed - settings managed via database
  {
    title: 'Planos',
    url: '/admin/plans',
    icon: Receipt,
    requiredRoles: ['portal_admin', 'license_manager']
  },
  {
    title: 'Configurações',
    url: '/admin/systems',
    icon: Layers,
    requiredRoles: ['portal_admin', 'license_manager']
  },
];

const integrationNavItems = [
  {
    title: 'Sistemas de Tags',
    url: '/admin/integrations/tag-systems',
    icon: Tags,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'APIs PortalONE',
    url: '/admin/integrations/api-portalone',
    icon: Globe,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'Notificações',
    url: '/admin/integrations/notifications',
    icon: Mail,
    requiredRoles: ['portal_admin']
  },
  {
    title: 'Webhook Echo',
    url: '/admin/integrations/echo',
    icon: Radio,
    requiredRoles: ['portal_admin']
  },
];

// PaymentONE and all systems removed - discontinued

const AdminLayout = () => {
  const { user, profile, signOut, loading, hasGlobalRole, globalRoles } = useAuth();
  const { setTheme } = useTheme();
  const location = useLocation();
  const { data: activeSystems = [], isLoading: loadingSystems } = useActiveSystems();

  // Helper para mapear códigos de sistema para ícones
  const getSystemIcon = (code: string) => {
    const iconMap: Record<string, any> = {
      fleet: Truck,
      production: Factory,
    };
    return iconMap[code] || Layers;
  };

  // Função auxiliar para verificar se uma rota está ativa
  const isActive = (path: string) => {
    // Match exato para /admin
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    
    const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const cleanCurrent = location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;
    
    // Match exato primeiro
    if (cleanCurrent === cleanPath) return true;
    
    // Para módulos/submódulos, só destacar se for filho direto (sem mais "/" após o próximo segmento)
    const regex = new RegExp(`^${cleanPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/[^/]+)?$`);
    return regex.test(cleanCurrent);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has any global role
  if (!globalRoles.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Acesso negado</h1>
          <p className="text-muted-foreground mb-4">
            Você não tem permissão para acessar o painel administrativo.
          </p>
          <Button asChild>
            <Link to="/">Voltar ao portal</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isIntegrationsActive = () => {
    return location.pathname.startsWith('/admin/integrations');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <Sidebar className="w-64">
          <SidebarContent>
            {/* Header */}
            <div className="p-4 border-b">
            <div className="flex items-center space-x-2">
              <img 
                src={portalOneLogo} 
                alt="PortalONE Suite" 
                className="h-8 w-auto object-contain" 
              />
              <div>
                <div className="font-bold text-sm">PortalONE Suite</div>
                <div className="text-xs text-muted-foreground">Admin Panel</div>
              </div>
            </div>
            </div>

            {/* Administração PortalONE - Collapsible */}
            <Collapsible defaultOpen={true} className="group/admin">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center justify-between p-2 hover:bg-accent rounded-md">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <span>Administração PortalONE</span>
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=closed]/admin:-rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {adminNavItems
                        .filter(item => 
                          item.requiredRoles.some(role => hasGlobalRole(role))
                        )
                        .map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild>
                            <Link 
                              to={item.url}
                              className={isActive(item.url) ? 'bg-accent text-accent-foreground' : ''}
                            >
                              <item.icon className="mr-2 h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>

            {/* Sistemas Liberados section removed - no systems remaining */}

          {/* Integrações - Collapsible */}
          <Collapsible defaultOpen={false} className="group/integrations">
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-2 hover:bg-accent rounded-md">
                  <div className="flex items-center gap-2">
                    <Plug className="h-4 w-4" />
                    <span>Integrações</span>
                  </div>
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=closed]/integrations:-rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {integrationNavItems
                      .filter(item => 
                        item.requiredRoles.some(role => hasGlobalRole(role))
                      )
                      .map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <Link 
                            to={item.url}
                            className={isActive(item.url) ? 'bg-accent text-accent-foreground' : ''}
                          >
                            <item.icon className="mr-2 h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        </SidebarContent>
          
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/admin/support">
                    <HelpCircle className="h-4 w-4" />
                    <span>Suporte</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <div className="flex-1 flex flex-col">
          {/* Top Header */}
          <header className="h-16 border-b bg-background flex items-center justify-between px-6">
            <div className="flex items-center space-x-4">
              <SidebarTrigger />
              <div>
                <h1 className="font-semibold">Painel Administrativo</h1>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <NotificationCenter />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {profile?.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block">{profile?.full_name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>Tema Claro</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>Tema Escuro</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>Tema do Sistema</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-6 bg-muted/50">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminLayout;